import { VehicleChecksApiService } from 'core/api';
import { useDialog } from 'core/providers/DialogProvider';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import { IVehicleCheckDao } from 'core/api/types/vehicle-checks.interface';
import { useCollection } from 'core/hooks/useCollection';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Checkbox from '@atlaskit/checkbox';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import ExportDialog from 'modules/dialogs/export-dialog';
import { PageElement } from 'core/utilities/interface-helpers';
import { orderBy, QueryConstraint, Timestamp, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { IButton } from 'shared/components/buttons/button-interface';

import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import dayjs from 'dayjs';
import { useSharedData } from 'core/providers/SharedDataProvider';

const VehicleChecksTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Direction', key: 'direction', templateId: 'direction', width: 10 },
  { label: 'Type', key: 'type', templateId: 'type', width: 10 },
  { label: 'Check Date', key: 'checkDate', templateId: 'checkDate', width: 10 },
  { label: 'Mileage', key: 'mileage', templateId: 'text', width: 10 },
  { label: 'Vehicle Registration', key: 'vehicleRegistration', templateId: 'vehicleRegistration', width: 10 },
  { label: 'Driver', key: 'driver', templateId: 'driver', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const VehicleChecksList = () => {
  const dialog = useDialog();
  const navigate = useNavigate();
  const { drivers, vehicles } = useSharedData();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<IVehicleCheckDao>({
    apiService: VehicleChecksApiService,
    constraints: [...constraints, orderBy('checkDate', 'desc')],
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '' && !filter.id.includes('.'))
        .map((filter) => {
          if (filter.id === 'driver') filter.id = 'driver.uid';
          if (filter.id.includes('From')) {
            const date = Timestamp.fromDate(
              dayjs(filter.value as string)
                .startOf('day')
                .toDate()
            );
            return where('checkDate', '>=', date);
          } else if (filter.id.includes('To')) {
            const date = Timestamp.fromDate(
              dayjs(filter.value as string)
                .endOf('day')
                .toDate()
            );
            return where('checkDate', '<=', date);
          }
          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return (
      <Checkbox isChecked={selectedRows.includes(vehicleCheck.uid)} onChange={(e) => rowChecked(e, vehicleCheck.uid)} />
    );
  };

  const actionCellTemplate = (vehicleCheck: IVehicleCheckDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => navigate(`/vehicle-checks/${vehicleCheck.uid}`)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='View'
      />
    </div>
  );

  const directionCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{vehicleCheck.direction === 'in' ? 'In' : 'Out'}</p>;
  };

  const checkTypeCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{vehicleCheck.type === 'daily' ? 'Daily' : 'Weekly'}</p>;
  };

  const checkDateCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{dayjs(vehicleCheck.checkDate.toDate()).format('HH:mm, DD/MM/YYYY')}</p>;
  };

  const vehicleRegistrationCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{vehicles?.find((vehicle) => vehicle.uid === vehicleCheck.vehicleUid)?.registration}</p>;
  };

  const driverCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{drivers?.find((driver) => driver.uid === vehicleCheck.driver.uid)?.fullName}</p>;
  };

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    { template: selectCellTemplate, id: 'select' },
    {
      template: directionCellTemplate,
      id: 'direction',
    },
    {
      template: checkTypeCellTemplate,
      id: 'type',
    },
    {
      template: vehicleRegistrationCellTemplate,
      id: 'vehicleRegistration',
    },
    {
      template: driverCellTemplate,
      id: 'driver',
    },
    {
      template: actionCellTemplate,
      id: 'action',
    },
    { template: checkDateCellTemplate, id: 'checkDate' },
  ];

  const headerActions: PageElement[] = [
    {
      key: 'exportLocations',
      element: (
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ExportDialog
                title='Export Vehicle Checks'
                apiService={VehicleChecksApiService}
                functionName={FirebaseFunctionNames.EXPORT_VEHICLE_CHECKS}
                filterQuery={filterQuery}
              />
            )
          }
          type='button'
          appearance='default'
          label='Export Vehicle Checks'
        />
      ),
    },
  ];

  const filters: Filter[] = [
    {
      id: 'dateFrom',
      label: 'Check Date From',
      date: true,
    },
    {
      id: 'dateTo',
      label: 'Check Date To',
      date: true,
    },
    {
      id: 'driver',
      label: 'Driver',
      options: drivers?.map((driver) => ({ value: driver.uid, label: driver.fullName })) || [],
    },
    {
      id: 'vehicleUid',
      label: 'Vehicle Registration',
      options: vehicles?.map((vehicle) => ({ value: vehicle.uid, label: vehicle.registration })) || [],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Vehicle Checks' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={VehicleChecksTableColumns}
        customTemplates={customTemplates}
        rows={tableRows}
        isLoading={loading}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default VehicleChecksList;
